/**
 * nav-prefetch.js — prefetch van de producten die je nú in beeld hebt.
 *
 * Waarom dit nodig is naast Shopify's eigen regels
 * ------------------------------------------------
 * Shopify levert platform-brede speculation rules voor Horizon via een
 * HTTP-header (horizon.specrules-*.json), met `eagerness: "moderate"`. Op
 * desktop werkt dat prima: hover 200 ms en het document staat klaar.
 *
 * Op mobiel niet. Twee redenen, allebei gemeten op /collections/all met echte
 * touch-scrollgebaren:
 *
 *   1. De mobiele heuristiek van `moderate` selecteert ankers "binnen 30% van
 *      de verticale afstand tot de vorige pointerdown". Na het scrollen is dat
 *      de plek waar je vinger lag tijdens het vegen — niet waar je daarna naar
 *      kijkt. Resultaat over 5 sessies: 1-2 prefetches per sessie, maar in
 *      geen enkele sessie een kaart die op dat moment in beeld stond.
 *   2. `moderate` mag maar 2 speculaties tegelijk hebben (FIFO). Op een grid
 *      met tientallen kaarten is dat structureel te weinig.
 *
 * Alleen `immediate` heeft een ruimere limiet (50). Die eagerness kent geen
 * eigen trigger — hij haalt op zodra de regel er staat — dus de selectie moet
 * van ons komen. Vandaar deze lijst: bij een scroll-stop pakken we de kaarten
 * die daadwerkelijk in beeld staan, begrensd, en zetten die in een list-rule.
 *
 * Kosten worden bewust geknepen: dit zijn echte Shopify-renders (~545 ms
 * servertijd) en echte bytes op de mobiele verbinding van de klant.
 * Vandaar de caps hieronder en het afhaken bij data-besparing of 2G.
 * Wil je dit uitzetten: haal de regel uit snippets/nav-feedback.liquid.
 */
(function () {
  'use strict';

  /** Hoeveel kaarten we per scroll-stop meenemen. */
  const PER_BATCH = 4;

  /**
   * Harde bovengrens per pagina, zodat lang scrollen niet blijft optellen.
   *
   * Afgestemd op paginatypes:
   * - Collectie met infinite scroll: ~24 producten per load.
   * - Categoriepagina (metaobject): meerdere collection/product/metaobject-
   *   lists, makkelijk ~50 tegels over de pagina.
   *
   * Cap 50 dekt die categoriegrids. Filter-/merklinks vreten de rest niet op
   * dankzij OVERIG_MAX hieronder. Gelijk aan Chrome's limiet van 50 voor
   * eagerness `immediate` op het paginabudget; exempt + intent tellen apart.
   */
  const PER_PAGE = 50;

  /** Rust na het scrollen voordat we iets ophalen. */
  const IDLE_MS = 300;

  /**
   * Welke links we überhaupt overwegen.
   *
   * /pages hoort erbij: 13% van de doorklikken vanaf een collectiepagina gaat
   * daarheen (o.a. de merkpagina's op /pages/merk/*), en Shopify's eigen regels
   * dekken dat pad ook. De rest van hun bereik (/blogs, /policies, /search)
   * laten we staan — daar is de heuristiek van `moderate` prima in, want dat
   * zijn losse links en geen grid.
   */
  const LINK_SELECTOR = 'a[href*="/products/"], a[href*="/collections/"], a[href*="/pages/"]';

  /**
   * Alleen links in de inhoud, niet in de chrome eromheen.
   *
   * `#header-group` bevat het mobiele menu en de servicebalk: negen
   * /pages-links plus de hele categorie-navigatie. Die staan op élke pagina en
   * zijn altijd zichtbaar, dus zonder deze afbakening slokten ze de eerste
   * batch op — gemeten kwam /pages/klantenservice/contact op drie van de vier
   * templates als eerste binnen. Precies de links waar Shopify's `moderate` al
   * goed in is, want het zijn er weinig en ze zijn niet gelijkvormig.
   *
   * Wat overblijft is de inhoud: kaarten, tegels, filterchips, advieslinks.
   */
  const CONTENT_ROOT = '#MainContent';

  /**
   * Pas ophalen als de pagina klaar en stil is.
   *
   * Stond eerst op een simpele timer van 300 ms na scriptuitvoering. Het script
   * is `defer`, dus dat viel nog vóór DOMContentLoaded: precies in het drukste
   * moment. De prefetches vochten dan om bandbreedte met de afbeeldingen van de
   * pagina die de bezoeker op dat moment zit te bekijken, en de leespas over
   * alle ankers kwam boven op een toch al belaste main thread. Merkbaar als een
   * cursor die te laat reageert en taps die niet aankomen.
   */
  let armed = false;

  const specRulesSupported =
    typeof HTMLScriptElement !== 'undefined' &&
    typeof HTMLScriptElement.supports === 'function' &&
    HTMLScriptElement.supports('speculationrules');

  if (!specRulesSupported) return;

  const connection = navigator.connection;
  if (connection) {
    if (connection.saveData === true) return;
    if (/(^|-)2g$/.test(connection.effectiveType || '')) return;
  }

  /** Paden die we nooit ophalen: muteren state of zijn geen productdocument. */
  const DENY_PATH = /^\/(cart|checkout|account|orders|a\/|apps\/|tools\/|services\/|discount\/|gift_cards\/)/;

  /** Querysleutels die een handeling uitvoeren in plaats van een pagina tonen. */
  const DENY_PARAM = /^(discount|return_url|checkout_url|token|logout|customer_posted|form_type)$/;

  /** @type {Set<string>} al aangevraagd (of de huidige pagina) */
  const requested = new Set([location.origin + location.pathname + location.search]);

  /**
   * @param {HTMLAnchorElement} anchor
   * @returns {string | null} de op te halen URL, of null
   */
  function target(anchor) {
    if (!anchor.href) return null;
    if (anchor.hasAttribute('download') || anchor.hasAttribute('data-no-prefetch')) return null;
    // Nieuw tabblad / externe CTA's (bestsellers, migratie-cards met ↗︎): geen document-prefetch.
    if (anchor.target && anchor.target !== '_self') return null;

    let url;
    try {
      url = new URL(anchor.href, location.href);
    } catch {
      return null;
    }

    // Alleen same-origin. www.homedesignshops.nl / railsopmaat / etc. vallen af —
    // die slots gaan naar de volgende interne link zodat de batch vol blijft.
    if (url.origin !== location.origin) return null;
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
    if (DENY_PATH.test(url.pathname)) return null;

    // Op de query filteren we per sleutel, niet op "alles behalve variant".
    // Zoekresultaten dragen Shopify's attributie mee
    // (?variant=…&_pos=1&_sid=…&_ss=r) en filterchips dragen filter.*/sort_by;
    // die weigeren betekende nul prefetches op /search en op elke gefilterde
    // collectie — samen een kwart van de doorklikken. Strippen is geen optie:
    // dan wijkt de opgehaalde URL af van de URL waar de klik heen gaat en is
    // de prefetch waardeloos. Dus alles toestaan, behalve wat een handeling
    // uitvoert in plaats van een document op te halen.
    for (const key of url.searchParams.keys()) {
      if (DENY_PARAM.test(key)) return null;
    }

    const clean = url.origin + url.pathname + url.search;
    return requested.has(clean) ? null : clean;
  }

  /**
   * Speculation-rules-scripts zijn onveranderlijk zodra ze in het document
   * staan: de inhoud aanpassen doet niets, en het element verwijderen annuleert
   * de speculatie. Elke batch krijgt dus een eigen script.
   *
   * @param {string[]} urls
   */
  function emit(urls) {
    const script = document.createElement('script');
    script.type = 'speculationrules';
    script.textContent = JSON.stringify({
      prefetch: [{ source: 'list', urls, eagerness: 'immediate' }],
    });
    document.head.append(script);
  }

  /**
   * Onder deze fractie van het grootste anker in beeld noemen we een link
   * klein: een filterchip, een menu-item, een tekstlink in een kruimelpad.
   * Zelfde verhouding die Chrome in zijn eigen mobiele heuristiek hanteert.
   */
  const MINOR_AREA_RATIO = 0.5;

  /**
   * Hoeveel van een batch naar die kleine links mag. Eén, want ze zijn met
   * weinig en mogen de kaarten niet verdringen — maar ze mogen ook niet
   * achteraan de rij belanden.
   */
  const MINOR_PER_BATCH = 1;

  /**
   * Wat er in welke volgorde wordt opgehaald.
   *
   * De onderste tier is de heuristiek waar dit bestand mee begon: oppervlak en
   * afstand tot het midden van het scherm. Die raadt alleen, en raadde
   * aantoonbaar mis — op een collectiepagina ging de eerste batch volledig op
   * aan de links bovenin terwijl er vier kaarten in beeld stonden. De tiers
   * erboven zeggen expliciet wat een pagina bedoelt, via `data-prefetch` op
   * section-wrappers (theme-setting `prefetch_tier`) of op individuele links
   * (chips, breadcrumbs).
   *
   * `exempt` betekent: telt niet mee in het paginabudget. Dat is bedoeld voor
   * de handvol links die op elke pagina de bedoeling zijn (logo, hoofdknoppen,
   * chips) en die dus nooit door een grid verdrongen mogen worden. Samen
   * begrensd door EXEMPT_MAX.
   *
   * `viewportOnly: false` haalt ook op wat nog niet in beeld staat. Alleen voor
   * die kleine, expliciete sets — een grid zou zo de hele pagina ophalen.
   */
  const TIERS = [
    {
      naam: 'logo',
      // Gaat naar de homepage, op elke pagina, en is er altijd maar één.
      selector: '#header-group a.header-logo',
      viewportOnly: false,
      exempt: true,
    },
    {
      naam: 'hoofdacties',
      selector: '[data-prefetch="hoofdacties"] a[href], a[data-prefetch="hoofdacties"]',
      viewportOnly: false,
      exempt: true,
    },
    {
      naam: 'tegels',
      // Alleen kaart-links, geen RTE/knoppen in dezelfde sectie (blog-intro,
      // "naar alle categorieën") — die stalen anders batch-slots.
      selector: [
        '[data-prefetch="tegels"] :is(product-card, .product-card, .collection-card, .resource-card, .card, slideshow-slide) a[href]',
        '[data-prefetch="tegels"] [class*="bento-grid-static__card"] a[href]',
        '[data-prefetch="tegels"] a[class*="bento-grid-static__link"]',
        '[data-prefetch="tegels"] a.product-card__link',
        '[data-prefetch="tegels"] .bento-box .collection-card a[href]',
      ].join(', '),
      viewportOnly: true,
      exempt: false,
    },
    {
      naam: 'overig',
      selector: LINK_SELECTOR,
      viewportOnly: true,
      exempt: false,
    },
  ];

  /** Hoeveel documenten de exempt-tiers samen buiten het paginabudget mogen. */
  const EXEMPT_MAX = 8;

  /**
   * Max slots voor de vangnet-tier. Voorkomt dat filter-/merklinks op een
   * collectiepagina de rest van PER_PAGE opeten nadat de productgrid klaar is.
   */
  const OVERIG_MAX = 4;

  let exemptUsed = 0;
  let overigUsed = 0;

  /**
   * Kandidaten per tier, in de volgorde waarin ze aan bod komen.
   *
   * Eén leespas over alle rects zonder tussentijdse writes, dus één forced
   * layout per scan. Gemeten 2,3 ms per scan bij 4x CPU-throttle.
   */
  function collectByTier() {
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const middle = viewportHeight / 2;
    const root = document.querySelector(CONTENT_ROOT);

    const inViewport = (rect) =>
      rect.width > 40 &&
      rect.bottom > 0 &&
      rect.top < viewportHeight &&
      rect.right > 0 &&
      rect.left < viewportWidth;

    /**
     * Verborgen desktop/mobiel-duplicaten (visibility_*) hebben wél
     * data-prefetch maar geen layout. Zonder deze check aten USP-rijen de
     * hele eerste batch op (stalenservice, bezorgen, …) en kwamen tegels
     * nooit aan de beurt.
     *
     * SHO-10266: `content-visibility: auto` slaat onder-de-vouw-secties over.
     * Kaarten zijn dan 0×0 en `checkVisibility({ checkOpacity: true })` faalt
     * ook op overlay-links. Placeholder van `.section--cv-auto` telt wél.
     *
     * @param {Element} el
     * @param {DOMRect} rect
     * @param {boolean} fromCvPlaceholder
     */
    const isRendered = (el, rect, fromCvPlaceholder) => {
      if (fromCvPlaceholder) return true;
      if (rect.width < 1 || rect.height < 1) return false;
      if (typeof el.checkVisibility === 'function') {
        return el.checkVisibility({
          checkOpacity: false,
          checkVisibilityCSS: true,
          contentVisibilityAuto: false,
        });
      }
      return true;
    };

    return TIERS.map((tier) => {
      // De expliciete tiers mogen buiten #MainContent kijken: het logo zit in
      // de header en de chips soms in een sectie eromheen.
      const scope = tier.viewportOnly ? root : document;
      if (!scope) return { tier, major: [], minor: [] };

      /** @type {Map<Element, number>} */
      const cvPlaceholderTaken = new Map();

      const gevonden = [...scope.querySelectorAll(tier.selector)]
        .filter((el) => el instanceof HTMLAnchorElement)
        .map((anchor) => {
          // Overlay-links (product-card__link) zijn absolute inset:0 — meet de
          // kaart als de link zelf 0×0 rapporteert door content-visibility.
          let rect = anchor.getBoundingClientRect();
          let fromCvPlaceholder = false;
          if (rect.width < 40 || rect.height < 40) {
            const host =
              anchor.closest(
                'product-card, .product-card, .collection-card, .resource-card, .card, slideshow-slide, [class*="bento-grid-static__card"]'
              ) || anchor.parentElement;
            if (host) rect = host.getBoundingClientRect();
          }
          if (rect.width < 40 || rect.height < 40) {
            const cv = anchor.closest('.section--cv-auto');
            if (cv) {
              const sr = cv.getBoundingClientRect();
              if (sr.height > 0 && sr.bottom > 0 && sr.top < viewportHeight) {
                const taken = cvPlaceholderTaken.get(cv) || 0;
                if (taken < PER_BATCH) {
                  cvPlaceholderTaken.set(cv, taken + 1);
                  fromCvPlaceholder = true;
                  rect = sr;
                }
              }
            }
          }
          return {
            anchor: /** @type {HTMLAnchorElement} */ (anchor),
            rect,
            area: rect.width * rect.height,
            fromCvPlaceholder,
          };
        })
        .filter(({ anchor, rect, fromCvPlaceholder }) => isRendered(anchor, rect, fromCvPlaceholder));

      const zichtbaar = gevonden.filter(({ rect }) => inViewport(rect));
      // viewportOnly: eerst wat in beeld staat. Zijn dat er na filteren van
      // externe URL's te weinig voor een volle batch, vul aan met de dichtstbij-
      // zijnde gerenderde links onder de vouw (zelfde tier) — anders blijven
      // categorie-cards op 3 hangen terwijl er wél een 4e interne is.
      let bruikbaar = tier.viewportOnly ? zichtbaar : gevonden;
      if (tier.viewportOnly && zichtbaar.length) {
        const urlsInView = new Set();
        for (const item of zichtbaar) {
          const url = target(item.anchor);
          if (url) urlsInView.add(url);
        }
        if (urlsInView.size < PER_BATCH) {
          const rest = gevonden
            .filter((item) => !zichtbaar.includes(item))
            .sort((a, b) => Math.abs(a.rect.top - middle) - Math.abs(b.rect.top - middle));
          bruikbaar = [...zichtbaar, ...rest];
        }
      }
      if (!bruikbaar.length) return { tier, major: [], minor: [] };

      bruikbaar.sort((a, b) => Math.abs(a.rect.top - middle) - Math.abs(b.rect.top - middle));

      // Alleen de vangnet-tier splitst nog op grootte; de tiers erboven zijn
      // al expliciet, daar zou grootte alleen maar ruis toevoegen.
      const largest = Math.max(...bruikbaar.map(({ area }) => area));
      /** @type {string[]} */
      const major = [];
      /** @type {string[]} */
      const minor = [];

      for (const { anchor, area } of bruikbaar) {
        const url = target(anchor);
        if (url === null) continue;
        const isKlein = tier.naam === 'overig' && area < largest * MINOR_AREA_RATIO;
        (isKlein ? minor : major).push(url);
      }

      return { tier, major, minor };
    });
  }

  /** Documenten die wél meetellen in het paginabudget. */
  let budgetUsed = 0;

  function scan() {
    if (!armed || document.prerendering || document.visibilityState === 'hidden') return;
    if (budgetUsed >= PER_PAGE && exemptUsed >= EXEMPT_MAX) return;

    /** @type {string[]} */
    let batch = [];

    const flush = () => {
      if (!batch.length) return;
      for (const url of batch) requested.add(url);
      emit(batch);
      batch = [];
    };

    /**
     * @param {string[]} urls
     * @param {{exempt: boolean, naam: string}} tier
     * @param {number} max
     */
    const take = (urls, tier, max) => {
      let taken = 0;
      for (const url of urls) {
        if (taken >= max) break;
        if (tier.exempt ? exemptUsed >= EXEMPT_MAX : budgetUsed >= PER_PAGE) break;
        if (tier.naam === 'overig' && overigUsed >= OVERIG_MAX) break;
        if (batch.includes(url) || requested.has(url)) continue;
        batch.push(url);
        if (tier.exempt) exemptUsed++;
        else {
          budgetUsed++;
          if (tier.naam === 'overig') overigUsed++;
        }
        taken++;
        // Batch vol → emit en ga door. Eerder: `break` op PER_BATCH stopte de
        // héle scan na 4 hoofdacties, waardoor tegels nooit geprefetcht werden
        // tot er gescrolld werd (en ook dan eerst de rest van de USP-duplicaten).
        if (batch.length >= PER_BATCH) flush();
      }
    };

    for (const { tier, major, minor } of collectByTier()) {
      // Nieuwe tier = nieuwe batch. Anders vulden 3 USP-hoofdacties de batch tot
      // 3 en stal de eerste tegel (of een knop) het 4e slot van de USP-emit.
      flush();

      if (tier.naam === 'overig') {
        // Vangnet: kleine links een vaste reservering zodat een grid ze niet
        // wegdrukt, daarna de grote, en de rest van de ruimte weer de kleine.
        take(minor, tier, MINOR_PER_BATCH);
        take(major, tier, PER_BATCH);
        take(minor, tier, PER_BATCH);
      } else if (tier.naam === 'tegels') {
        // Kaarten: batch van PER_BATCH emitten en doorgaan tot de kandidaten
        // of het paginabudget op zijn — niet stoppen na één batch van 4.
        take(major, tier, PER_PAGE);
      } else {
        // logo / hoofdacties: één batch per scan is genoeg.
        take(major, tier, PER_BATCH);
      }
    }

    flush();
  }

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  let idleTimer;

  /** Debounce: bij scrollen willen we juist wachten tot de vinger stilligt. */
  function scheduleScan() {
    clearTimeout(idleTimer);
    idleTimer = setTimeout(scan, IDLE_MS);
  }

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  let throttleTimer;

  /**
   * Throttle in plaats van debounce, voor triggers die in een aanhoudende
   * stroom komen. Een lazy ladende pagina muteert de DOM continu; met een
   * debounce werd de timer eindeloos teruggezet en vuurde de scan nooit — op
   * een uitgescrolde collectiepagina zakte de dekking daardoor van 26 naar 8.
   */
  function throttledScan() {
    if (throttleTimer) return;
    throttleTimer = setTimeout(() => {
      throttleTimer = undefined;
      scan();
    }, IDLE_MS);
  }

  window.addEventListener('scroll', scheduleScan, { passive: true });
  window.addEventListener('resize', scheduleScan, { passive: true });
  // Filters en paginering vervangen de grid zonder scroll-event.
  document.addEventListener('shopify:section:load', scheduleScan);
  // SHO-10266: unskip na scroll-stop vuurt geen scroll-event. Zonder deze
  // scan blijven tegels in `.section--cv-auto` op nul prefetches staan.
  document.addEventListener('contentvisibilityautostatechange', scheduleScan);

  /**
   * Inhoud die ná het laden verschijnt, meldt zich niet met een scroll- of
   * section-event. Op /search vult Algolia de resultaten pas ná ons
   * scanmoment; zonder dit bleef die pagina op nul prefetches staan.
   * De scan zelf is al gedebouncet op IDLE_MS, dus dit kan niet gaan stuiteren.
   */
  function watchContent() {
    const root = document.querySelector(CONTENT_ROOT);
    if (!root || typeof MutationObserver !== 'function') return;
    new MutationObserver(throttledScan).observe(root, { childList: true, subtree: true });
  }

  /**
   * Vangnet op expliciete intentie.
   *
   * Alles hierboven leidt af wat je waarschijnlijk gaat aanklikken. Blijft de
   * muis op een link staan, of raak je hem aan, dan is dat geen afleiding meer
   * maar een signaal. Daarom buiten het paginabudget om, met een eigen kleine
   * limiet.
   *
   * Shopify's `moderate` doet dit op desktop ook, maar mag maar twee
   * speculaties tegelijk hebben: wie over een grid beweegt, gooit steeds de
   * vorige weg. Bij aanraking levert dit de 80-150 ms tussen neerzetten en
   * loslaten op — niet veel, maar het is gratis.
   */
  const INTENT_MAX = 12;
  const INTENT_DWELL_MS = 80;
  let intentUsed = 0;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  let intentTimer;

  /** @param {Element | null} anchor */
  function prefetchIntent(anchor) {
    if (document.prerendering || document.visibilityState === 'hidden') return;
    if (!anchor || intentUsed >= INTENT_MAX) return;
    const url = target(/** @type {HTMLAnchorElement} */ (anchor));
    if (url === null) return;
    requested.add(url);
    intentUsed++;
    emit([url]);
  }

  /** @param {Event} event */
  function anchorFrom(event) {
    const el = /** @type {Element | null} */ (event.target);
    return el && typeof el.closest === 'function' ? el.closest('a[href]') : null;
  }

  function watchIntent() {
    // pointerenter bubbelt niet, dus pointerover met een korte dwell: anders
    // vuurt elke muis die over een grid strijkt de hele rij af.
    document.addEventListener(
      'pointerover',
      (event) => {
        clearTimeout(intentTimer);
        const anchor = anchorFrom(event);
        if (!anchor) return;
        intentTimer = setTimeout(() => prefetchIntent(anchor), INTENT_DWELL_MS);
      },
      { passive: true }
    );

    // Alleen muis: op touch start elke veeg die op een kaart begint met
    // pointerdown en zou zo het intent-budget verbranden. Zichtbare tegels
    // dekt de viewport-scan al.
    document.addEventListener(
      'pointerdown',
      (event) => {
        if (event.pointerType === 'touch' || event.pointerType === 'pen') return;
        prefetchIntent(anchorFrom(event));
      },
      { passive: true }
    );
  }

  /** Scherpstellen zodra de pagina zelf klaar is en de main thread rust heeft. */
  function arm() {
    armed = true;
    watchContent();
    watchIntent();
    scheduleScan();
  }

  function armWhenQuiet() {
    if (document.prerendering) {
      document.addEventListener('prerenderingchange', armWhenQuiet, { once: true });
      return;
    }
    if (typeof requestIdleCallback === 'function') {
      // De timeout is een bovengrens: op een pagina die maar bezig blijft
      // wachten we niet eindeloos af.
      requestIdleCallback(arm, { timeout: 2000 });
    } else {
      setTimeout(arm, 500);
    }
  }

  if (document.readyState === 'complete') armWhenQuiet();
  else window.addEventListener('load', armWhenQuiet, { once: true });
})();
